import React from 'react';
import { PrimaryButton, TransparentButton } from '@bloomberg/media-ui-core';
import { Lock } from '@bloomberg/media-ui-icons';
import { Overlay, type OverlayProps } from '@bloomberg/media-ui-overlay';
import RegUIClient from 'reg-ui-client';

import { LABEL_KEYS, useIntl, useTranslateMessage } from '../i18n';
import { isJapaneseLocale } from '../utils';

import styles from './styles.module.css';

const DEFAULT_IN_SOURCE = 'subscriber-only-benefit';

export interface SubscriberOnlyBenefitOverlayProps
  extends Omit<Omit<OverlayProps, 'headerText'>, 'Icon'> {
  client?: RegUIClient;
  headerText?: string;
  inSource?: string;
  children?: React.ReactNode;
}

export const SubscriberOnlyBenefitOverlay: React.FC<
  SubscriberOnlyBenefitOverlayProps
> = (props) => {
  const { client, inSource = DEFAULT_IN_SOURCE, children, placement } = props;

  const translateMessage = useTranslateMessage();
  const { locale } = useIntl();

  const isJapanese = isJapaneseLocale(locale);

  const headerText =
    props.headerText ??
    translateMessage({
      id: LABEL_KEYS['media-ui-subscriptions_subscriberBenefit'],
      defaultMessage: 'Subscriber Benefit',
    });

  const componentChildren = children ?? (
    <p className={styles.defaultText}>
      {translateMessage({
        id: LABEL_KEYS['media-ui-subscriptions_subscriberOnlyFeature'],
        defaultMessage:
          'This feature is only available to bloomberg.com subscribers.',
      })}
    </p>
  );

  const isNewsletterSubscriber =
    (client?.getEntitlementFeatures() || []).length > 0;
  const jpPrefix = isJapanese ? '/jp' : '';
  const primaryLinkUrl = isNewsletterSubscriber
    ? `${jpPrefix}/portal/subscription?triggerSubscriptionUpgradeModal=true&in_source=${inSource}`
    : `${jpPrefix}/subscriptions?in_source=${inSource}`;

  const primaryButtonText = isNewsletterSubscriber
    ? translateMessage({
        id: LABEL_KEYS['media-ui-subscriptions_upgrade'],
        defaultMessage: 'Upgrade',
      })
    : translateMessage({
        id: LABEL_KEYS['media-ui-subscriptions_subscribe'],
        defaultMessage: 'Subscribe',
      });

  return (
    <Overlay
      {...props}
      headerText={headerText}
      Icon={<Lock />}
      placement={placement}
    >
      <>
        {componentChildren}
        <div className={styles.buttonContainer}>
          <PrimaryButton version="phoenix" linkUrl={primaryLinkUrl} fullWidth>
            {primaryButtonText}
          </PrimaryButton>
          {!client?.isLoggedIn() && (
            <TransparentButton
              version="phoenix"
              fullWidth
              className={styles.signInButton}
              onClick={() =>
                client?.linkToSignIn({
                  in_source: inSource,
                  leadSource: inSource,
                })
              }
            >
              {translateMessage({
                id: LABEL_KEYS['media-ui-subscriptions_signIn'],
                defaultMessage: 'Sign In',
              })}
            </TransparentButton>
          )}
        </div>
      </>
    </Overlay>
  );
};
